import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * ConfettiBurst — full-screen ribbon confetti animation. Fires once when
 * `trigger` changes to a fresh truthy value. Auto-cleans after ~3s.
 *
 * Colors are drawn from the app's earthy palette + a few celebration hues.
 * Rendered via portal so it always overlays modals correctly.
 */

const COLORS = [
  "#2D5A40", // deep forest green
  "#E09E50", // amber
  "#437A55", // moss green
  "#F0C6A0", // peach
  "#C88B6E", // terracotta
  "#FBF5EB", // cream
  "#A16A25", // caramel
  "#8B4A44", // rose
];

export default function ConfettiBurst({ trigger, count = 60 }) {
  const [runId, setRunId] = useState(null);

  useEffect(() => {
    if (!trigger) return;
    setRunId(Date.now());
    const t = setTimeout(() => setRunId(null), 3000);
    return () => clearTimeout(t);
  }, [trigger]);

  if (runId === null) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  const pieces = Array.from({ length: count }, (_, i) => {
    // Start slightly above the top edge, spread across viewport width
    const x  = (Math.random() - 0.5) * vw;                       // horizontal start offset from centre
    const dx = (Math.random() - 0.5) * vw * 0.6;                 // sideways drift
    const fall = vh * (0.85 + Math.random() * 0.25);             // fall distance
    const rot = (Math.random() * 3 + 1) * 360 * (Math.random() < 0.5 ? -1 : 1);
    const delay = Math.random() * 300;                           // stagger
    const color = COLORS[i % COLORS.length];
    const shape = i % 3 === 0 ? "50%" : "2px";                   // some circles, mostly ribbons
    const w = 6 + Math.random() * 6;
    const h = shape === "50%" ? w : 10 + Math.random() * 8;
    return { i, x, dx, fall, rot, delay, color, w, h, shape };
  });

  return createPortal(
    <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-[10000]" data-testid="confetti-burst">
      {pieces.map((p) => (
        <span
          key={p.i}
          className="confetti-piece"
          style={{
            "--x": `${p.x}px`,
            "--dx": `${p.dx}px`,
            "--fall": `${p.fall}px`,
            "--rot": `${p.rot}deg`,
            animationDelay: `${p.delay}ms`,
            background: p.color,
            width: p.w,
            height: p.h,
            borderRadius: p.shape,
          }}
        />
      ))}
    </div>,
    document.body,
  );
}
